import { memo, useEffect, useState } from "react";
import styled from "styled-components";
import { FaImage } from "react-icons/fa";
import MatplotlibQuickPreview from "../document-processing/MatplotlibQuickPreview";

const LiveCodePreview = memo(MatplotlibQuickPreview);

export default function QuestionAssetEditorPreview({ asset, inputMode }) {
  const urlMode = inputMode === "url";
  const code = urlMode ? "" : String(asset.source_code || "").trim();
  const [previewCode, setPreviewCode] = useState(code);
  const [failedImage, setFailedImage] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setPreviewCode(code), 250);
    return () => clearTimeout(timer);
  }, [code]);

  const generatedImage = String(
    asset._preview_url || asset._previewUrl || "",
  ).trim();
  const matchesCode = String(asset._preview_source_code || "").trim() === code;
  const svgMode = !urlMode && asset.source_language === "svg";
  const image = svgMode
    ? code
      ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(code)}`
      : ""
    : (!urlMode && generatedImage && matchesCode ? generatedImage : "") ||
      (inputMode === "code" && !code ? "" : String(asset.url || "").trim());
  const python =
    Boolean(code) &&
    (!asset.source_language || asset.source_language === "python");
  const status =
    svgMode && code
      ? "SVG 預覽"
      : image
        ? asset.url === image
          ? "已儲存圖片"
          : "產生的圖片"
        : python
          ? "即時預覽"
          : "尚無預覽";

  return (
    <PreviewPanel aria-label="圖片素材預覽">
      <PreviewHeading>
        <strong>
          <FaImage /> 圖片預覽
        </strong>
        <span>{status}</span>
      </PreviewHeading>
      <PreviewCanvas aria-busy={python && previewCode !== code}>
        {image ? (
          failedImage === image ? (
            <EmptyPreview role="status">
              <FaImage />
              <strong>圖片無法載入</strong>
              <span>請確認圖片連結或 SVG 原始碼是否正確。</span>
            </EmptyPreview>
          ) : (
            <img
              src={image}
              alt={asset.alt_text || "圖片素材預覽"}
              onError={() => setFailedImage(image)}
            />
          )
        ) : python ? (
          <LiveCodePreview
            code={previewCode}
            pending
            compact
            emptyTitle="這段程式碼尚無可預覽內容"
            unsupportedLabel="用 Vim 編輯並產生圖片"
          />
        ) : (
          <EmptyPreview>
            <FaImage />
            <strong>
              {svgMode
                ? "貼上 SVG 原始碼"
                : urlMode
                  ? "貼上圖片連結"
                  : code
                    ? "此語言尚不支援即時預覽"
                    : "從程式碼開始繪圖"}
            </strong>
            <span>
              {svgMode
                ? "貼上完整的 SVG 原始碼，即可查看圖片。"
                : urlMode
                  ? "輸入圖片 URL 後，預覽會顯示在這裡。"
                  : code
                    ? "可切換圖片 URL 顯示圖片預覽。"
                    : "輸入 Python 繪圖程式碼，即時查看圖片。"}
            </span>
          </EmptyPreview>
        )}
      </PreviewCanvas>
      <PreviewNote>
        {image
          ? svgMode
            ? "SVG 圖片依目前原始碼即時顯示。"
            : urlMode
              ? "圖片來自目前填入的 URL。"
              : "目前顯示圖片素材；可用 Vim 開啟大圖與程式碼編輯。"
          : python
            ? "即時預覽僅供參考；完整結果可用 Vim 編輯，再按 Space + W 產生圖片。"
            : urlMode
              ? "支援可直接開啟的圖片連結。"
              : svgMode
                ? "SVG 圖片會隨原始碼更新。"
                : "支援 Python 繪圖程式碼即時預覽。"}
      </PreviewNote>
    </PreviewPanel>
  );
}

const PreviewPanel = styled.section`
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--app-border, #e1e5eb);
  border-radius: 10px;
`;
const PreviewHeading = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--app-border, #e1e5eb);
  font-size: 12px;
  strong {
    display: flex;
    align-items: center;
    gap: 7px;
    color: var(--app-text, #263348);
  }
  > span {
    border-radius: 20px;
    padding: 3px 8px;
    color: var(--app-muted-text, #64748b);
    background: var(--app-surface-hover, #f1f5f9);
  }
`;
const PreviewCanvas = styled.div`
  min-width: 0;
  min-height: 240px;
  flex: 1;
  display: grid;
  place-items: center;
  padding: 16px;
  background-color: #f8fafc;
  background-image: radial-gradient(#dce2ea 0.7px, transparent 0.7px);
  background-size: 12px 12px;
  > img {
    display: block;
    max-width: 100%;
    max-height: 360px;
    object-fit: contain;
    border-radius: 4px;
    background: white;
  }
  > div {
    width: 100%;
    min-width: 0;
  }
  svg[role="img"] {
    width: 100%;
    height: auto;
    max-height: 360px;
  }
`;
const EmptyPreview = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 24px 12px;
  color: #64748b;
  text-align: center;
  > svg {
    width: 28px;
    height: 28px;
    color: #94a3b8;
  }
  strong {
    font-size: 13px;
    font-weight: 600;
  }
  span {
    font-size: 12px;
    line-height: 1.7;
  }
`;
const PreviewNote = styled.p`
  margin: 0;
  padding: 10px 14px;
  border-top: 1px solid var(--app-border, #e1e5eb);
  color: var(--app-muted-text, #64748b);
  font-size: 11px;
  line-height: 1.7;
`;
